import { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MessageSquare, Building2, Paperclip, X, Upload, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EmpresaOuvidoriaForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [empresa, setEmpresa] = useState(null);

  const tipos = [
    { value: "sugestao", label: t("ombudsmanForm.types.suggestion"), desc: t("ombudsmanForm.types.suggestionDesc"), icon: "💡" },
    { value: "reclamacao", label: t("ombudsmanForm.types.complaint"), desc: t("ombudsmanForm.types.complaintDesc"), icon: "📢" },
    { value: "elogio", label: t("ombudsmanForm.types.compliment"), desc: t("ombudsmanForm.types.complimentDesc"), icon: "⭐" },
    { value: "informacao", label: t("ombudsmanForm.types.information"), desc: t("ombudsmanForm.types.informationDesc"), icon: "❓" },
  ];
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    tipo: "",
    nome: "",
    email: "",
    telefone: "",
    descricao: "",
    aceita_lgpd: false,
  });
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    fetchEmpresa();
  }, [slug]);

  const fetchEmpresa = async () => {
    try {
      const res = await axios.get(`${API_URL}/empresas/${slug}/check`);
      if (res.data.exists) {
        setEmpresa(res.data);
      } else {
        navigate("/");
        toast.error(t("common.error"));
      }
    } catch (error) {
      navigate("/");
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt'];
      if (!allowed.includes(ext)) {
        toast.error(t("ombudsmanForm.errors.fileNotAllowed", { filename: file.name }));
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t("ombudsmanForm.errors.fileTooLarge", { filename: file.name }));
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.aceita_lgpd) {
      toast.error(t("ombudsmanForm.errors.lgpdRequired"));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/empresas/${slug}/ouvidoria`, formData);
      const { protocolo, sla_prazo } = response.data;
      
      setResultado({ protocolo, sla_prazo });
      setStep(3);
      toast.success(t("ombudsmanForm.messages.success"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("ombudsmanForm.errors.submitError"));
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.tipo;
    if (step === 2) {
      return formData.nome && formData.email && formData.descricao.length >= 20 && formData.aceita_lgpd;
    }
    return false;
  };

  const formatDate = (isoDate) => {
    const locale = t("common.locale") === "en" ? "en-US" : "pt-BR";
    return new Date(isoDate).toLocaleDateString(locale);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{empresa?.nome ? `Ouvidoria - ${empresa.nome}` : 'Canal de Ouvidoria'}</title>
        <meta name="description" content={`Canal de Ouvidoria da ${empresa?.nome || 'empresa'}. Envie sugestões, reclamações ou elogios. Sua manifestação será tratada com confidencialidade.`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="bg-[#f5a623]">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to={`/${slug}`} className="inline-flex items-center gap-2 text-[#1a1a2e]/70 hover:text-[#1a1a2e] mb-4">
            <ArrowLeft className="w-4 h-4" />
            {t("ombudsmanForm.backToChannel")}
          </Link>
          <div className="flex items-center gap-3">
            {empresa?.logo_url ? (
              <img src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)} alt="" className="h-8" />
            ) : (
              <MessageSquare className="w-8 h-8 text-[#1a1a2e]" />
            )}
            <div>
              <h1 className="text-xl font-bold text-[#1a1a2e]">{empresa?.nome || t("ombudsmanForm.title")}</h1>
              <p className="text-sm text-[#1a1a2e]/70">{t("ombudsmanForm.subtitle")}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Progress */}
        {step < 3 && (
          <div className="flex items-center justify-center mb-8">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? "bg-[#f5a623] text-[#1a1a2e]" : "bg-slate-200 text-slate-500"
                }`}>
                  {s}
                </div>
                {s < 2 && <div className={`w-16 h-1 ${step > s ? "bg-[#f5a623]" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Type */}
        {step === 1 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <h2 className="text-xl font-bold text-[#1a1a2e] mb-2">{t("ombudsmanForm.step1.title")}</h2>
            <p className="text-slate-500 mb-6">{t("ombudsmanForm.step1.subtitle")}</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tipos.map((tipo) => (
                <button
                  key={tipo.value}
                  type="button"
                  onClick={() => handleChange("tipo", tipo.value)}
                  data-testid={`tipo-${tipo.value}`}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.tipo === tipo.value
                      ? "border-[#f5a623] bg-[#f5a623]/5"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className="text-2xl mb-2 block">{tipo.icon}</span>
                  <span className="font-medium text-[#1a1a2e] block">{tipo.label}</span>
                  <span className="text-sm text-slate-500">{tipo.desc}</span>
                </button>
              ))}
            </div>

            <div className="mt-8 flex justify-end">
              <Button
                onClick={() => setStep(2)}
                disabled={!canProceed()}
                className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e]"
              >
                {t("ombudsmanForm.form.next")}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Form */}
        {step === 2 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <h2 className="text-xl font-bold text-[#1a1a2e] mb-2">{t("ombudsmanForm.step2.title")}</h2>
            <p className="text-slate-500 mb-6">{t("ombudsmanForm.step2.subtitle")}</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome">{t("ombudsmanForm.form.name")} *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => handleChange("nome", e.target.value)}
                    placeholder={t("ombudsmanForm.form.namePlaceholder")}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t("ombudsmanForm.form.email")} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder={t("ombudsmanForm.form.emailPlaceholder")}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="telefone">{t("ombudsmanForm.form.phone")} <span className="text-slate-400">({t("ombudsmanForm.form.phoneOptional")})</span></Label>
                <Input
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => handleChange("telefone", e.target.value)}
                  placeholder={t("ombudsmanForm.form.phonePlaceholder")}
                />
              </div>

              <div>
                <Label htmlFor="descricao">{t("ombudsmanForm.form.description")} *</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => handleChange("descricao", e.target.value)}
                  placeholder={t("ombudsmanForm.form.descriptionPlaceholder")}
                  className="min-h-[120px]"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.descricao.length}/20 {t("ombudsmanForm.form.minChars")}
                </p>
              </div>

              {/* File Upload */}
              <div>
                <Label>{t("ombudsmanForm.form.attachments")} <span className="text-slate-400">({t("ombudsmanForm.form.attachmentsOptional")})</span></Label>
                <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
                  />
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Paperclip className="w-4 h-4 mr-2" />
                    {t("ombudsmanForm.form.selectFiles")}
                  </Button>
                </div>
                
                {files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
                        <span className="text-sm truncate">{file.name}</span>
                        <button type="button" onClick={() => removeFile(index)} className="text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="lgpd"
                  checked={formData.aceita_lgpd}
                  onCheckedChange={(checked) => handleChange("aceita_lgpd", checked)}
                />
                <label htmlFor="lgpd" className="text-sm text-slate-600">
                  {t("ombudsmanForm.form.lgpdConsent")}
                </label>
              </div>

              <div className="flex justify-between pt-4">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  {t("ombudsmanForm.form.back")}
                </Button>
                <Button
                  type="submit"
                  disabled={!canProceed() || loading}
                  className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e]"
                >
                  {loading ? t("ombudsmanForm.form.submitting") : t("ombudsmanForm.form.submit")}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && resultado && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-[#1a1a2e] mb-2">{t("ombudsmanForm.success.title")}</h2>
            <p className="text-slate-500 mb-8">{t("ombudsmanForm.success.description")}</p>

            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <div className="mb-4">
                <span className="text-sm text-slate-500">{t("ombudsmanForm.success.protocol")}</span>
                <p className="text-2xl font-mono font-bold text-[#1a1a2e]">{resultado.protocolo}</p>
              </div>
              <div>
                <span className="text-sm text-slate-500">{t("ombudsmanForm.success.deadline")}</span>
                <p className="text-lg font-medium text-[#1a1a2e]">{formatDate(resultado.sla_prazo)}</p>
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-6">
              {t("ombudsmanForm.success.emailNotice")} <strong>{formData.email}</strong>
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={`/${slug}`} className="flex-1">
                <Button className="w-full bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e]">
                  {t("ombudsmanForm.success.backToChannel")}
                </Button>
              </Link>
            </div>

            {/* LGPD Link */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-2">Direito ao Esquecimento (LGPD)</p>
              <Link 
                to={`/${slug}/ouvidoria/esquecimento`}
                className="text-sm text-red-600 hover:text-red-700 hover:underline"
              >
                Solicitar exclusão de meus dados
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
